import { hasAnyPermission } from '@platform/auth/permissions';
import { useCurrentFeatureFlags } from '@platform/feature-flags';
import { Divider, Icon, NavLink, SideBarButton } from '@shared/ui';
import clsx from 'clsx';
import type { FC } from 'react';
import { Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import AdminSearchButton from './AdminSearchButton';
import { adminHomeLink, findActiveAdminNav, visibleAdminNavGroups } from './adminNavGroups';
import { useCollapsedAdminGroups } from './useCollapsedAdminGroups';

type AdminNavProps = {
  permissions: string[];
  roles: string[];
  isExpanded: boolean;
  /**
   * Opens the admin search palette. Optional so the nav still renders standalone
   * (Storybook, tests) without a palette wired up behind it.
   */
  onOpenSearch?: () => void;
};

const AdminNav: FC<AdminNavProps> = ({ permissions, roles, isExpanded, onOpenSearch }) => {
  const location = useLocation();
  const { globallyEnabledFlags: enabledFlags } = useCurrentFeatureFlags();
  const { isCollapsed, toggleGroup } = useCollapsedAdminGroups();

  const groups = visibleAdminNavGroups({ permissions, roles, enabledFlags });
  const canSeeHome = hasAnyPermission(permissions, [...adminHomeLink.requiredPermissions], roles);

  if (!canSeeHome && groups.length === 0) {
    return null;
  }

  // Admin routes nest, so the active link is resolved by longest match rather than by each
  // link testing the pathname itself — otherwise /admin/appointments/review lights up both
  // Appointments and Review Queue.
  const active = findActiveAdminNav(location.pathname, groups);
  const isLinkActive = (route: string) => route === active?.link.route;
  // The group you are standing in is always open, so navigating never lands you in a folded list.
  const isGroupOpen = (key: string) => key === active?.group.key || !isCollapsed(key);

  if (!isExpanded) {
    return (
      <>
        {onOpenSearch && <AdminSearchButton isExpanded={false} onClick={onOpenSearch} />}
        {canSeeHome && (
          <SideBarButton
            icon={adminHomeLink.icon}
            route={adminHomeLink.route}
            title={adminHomeLink.label}
          />
        )}
        {groups.map((group) => (
          <Fragment key={group.key}>
            <Divider className="w-8" />
            {group.links.map((link) => (
              <SideBarButton
                icon={link.icon}
                isActiveOverride={isLinkActive(link.route)}
                key={link.route}
                route={link.route}
                title={link.label}
              />
            ))}
          </Fragment>
        ))}
      </>
    );
  }

  return (
    <nav aria-label="Admin navigation" className="flex w-full flex-col gap-1">
      {onOpenSearch && <AdminSearchButton isExpanded onClick={onOpenSearch} />}
      {canSeeHome && (
        <NavLink
          compact
          exactMatch
          icon={adminHomeLink.icon}
          label={adminHomeLink.label}
          route={adminHomeLink.route}
        />
      )}

      {groups.map((group) => {
        const open = isGroupOpen(group.key);
        return (
          <div key={group.key}>
            <button
              aria-expanded={open}
              // This app does not run Tailwind preflight, so the UA button styles are reset here.
              className="flex w-full cursor-pointer appearance-none items-center gap-0.5 border-0 bg-transparent px-2.5 pb-1 pt-3 text-left font-sans text-[10px] font-bold uppercase tracking-[0.11em] text-sage-200/60 transition-colors hover:text-sage-200"
              onClick={() => toggleGroup(group.key)}
              type="button"
            >
              {group.label}
              <Icon className={clsx('text-[14px] transition-transform', !open && '-rotate-90')}>
                expand_more
              </Icon>
            </button>

            {open && (
              <div className="flex flex-col gap-1">
                {group.links.map((link) => (
                  <NavLink
                    compact
                    icon={link.icon}
                    isActiveOverride={isLinkActive(link.route)}
                    key={link.route}
                    label={link.label}
                    route={link.route}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default AdminNav;
